import { ContaPacienteResumo } from '../interfaces/dados-pagamento';
import { HistoricoNotaFiscal, HistoricoOrcamentoLinha, HistoricoPlano } from '../interfaces/historico-paciente';

export interface OrcamentoAgrupado {
  nr: number;
  nrAtendimento: number | null;
  data: string;
  status: string;
  medico: string;
  procedimentos: HistoricoOrcamentoLinha[];
  executados: number;
  pendentes: number;
  vlVista: number;
  vlCartao: number;
  valoresDiferem: boolean;
}

const DIA_MS = 86_400_000;

// dd/MM/yyyy ou yyyy-MM-dd -> epoch em UTC (NaN se invalida)
function paraUtc(s: string): number {
  const br = /^(\d{2})\/(\d{2})\/(\d{4})/.exec(s ?? '');
  if (br) return Date.UTC(+br[3], +br[2] - 1, +br[1]);
  const iso = /^(\d{4})-(\d{2})-(\d{2})/.exec(s ?? '');
  return iso ? Date.UTC(+iso[1], +iso[2] - 1, +iso[3]) : NaN;
}

export function agruparOrcamentos(linhas: HistoricoOrcamentoLinha[]): OrcamentoAgrupado[] {
  const grupos = new Map<number, OrcamentoAgrupado & { somaVista: number; somaCartao: number; totVista: number; totCartao: number }>();
  const vistos = new Set<string>();
  for (const l of linhas) {
    let g = grupos.get(l.nrSequenciaOrcamento);
    if (!g) {
      g = {
        nr: l.nrSequenciaOrcamento, nrAtendimento: l.nrAtendimento, data: l.dtOrcamento, status: l.dsStatusOrcamento, medico: l.nmMedico,
        procedimentos: [], executados: 0, pendentes: 0, vlVista: 0, vlCartao: 0, valoresDiferem: false,
        somaVista: 0, somaCartao: 0, totVista: 0, totCartao: 0,
      };
      grupos.set(l.nrSequenciaOrcamento, g);
    }
    // o back repete o procedimento quando o atendimento tem 2+ agendas
    const chave = `${l.nrSequenciaOrcamento}|${l.nrSeqOrcProc}`;
    if (vistos.has(chave)) continue;
    vistos.add(chave);
    g.procedimentos.push(l);
    if (l.executado) g.executados++; else g.pendentes++;
    g.somaVista += l.vlVista ?? 0;
    g.somaCartao += l.vlCartao ?? 0;
    // o total do orcamento vem repetido em cada linha; sem ele vale a soma das linhas
    g.totVista = l.vlOrcamentoVista || g.totVista;
    g.totCartao = l.vlOrcamentoCartao || g.totCartao;
  }
  return [...grupos.values()].map(({ somaVista, somaCartao, totVista, totCartao, ...g }) => {
    const vlVista = totVista || somaVista;
    const vlCartao = totCartao || somaCartao;
    return { ...g, vlVista, vlCartao, valoresDiferem: Math.abs(vlVista - vlCartao) > 0.005 };
  });
}

export function contasDaJanela(contas: ContaPacienteResumo[], hojeIso: string): { visiveis: ContaPacienteResumo[]; ocultas: ContaPacienteResumo[] } {
  const hoje = paraUtc(hojeIso);
  const visiveis: ContaPacienteResumo[] = [];
  const ocultas: ContaPacienteResumo[] = [];
  for (const c of contas) {
    // mesmo criterio do dashboard (contaDefinitiva)
    const provisoria = !c.statusConta?.toLowerCase().includes('definit');
    const dias = (hoje - paraUtc(c.dtPeriodoInicial)) / DIA_MS;
    (provisoria || dias <= 40 ? visiveis : ocultas).push(c);
  }
  return { visiveis, ocultas };
}

export function notasRecentes(notas: HistoricoNotaFiscal[], hojeIso: string): { visiveis: HistoricoNotaFiscal[]; ocultas: HistoricoNotaFiscal[] } {
  const h = new Date(paraUtc(hojeIso));
  const limite = Date.UTC(h.getUTCFullYear(), h.getUTCMonth() - 6, h.getUTCDate());
  const visiveis: HistoricoNotaFiscal[] = [];
  const ocultas: HistoricoNotaFiscal[] = [];
  for (const n of notas) (paraUtc(n.dtEmissao) >= limite ? visiveis : ocultas).push(n);
  return { visiveis, ocultas };
}

export function rotuloPlano(p: HistoricoPlano | null): { texto: string; particular: boolean } {
  const m = p?.modalidade ?? '';
  const rotulo = (base: string) => {
    // DS_CATEGORIA costuma repetir o prefixo ("B2B - BLACK SiMCo")
    const cat = (p!.dsCategoria ?? '').trim().replace(/^(b2b|cart[aã]o\s*sim)(?=[\s\-·]|$)\s*[-·]?\s*/i, '').trim();
    return { texto: cat ? `${base} · ${cat}` : base, particular: false };
  };
  if (m.startsWith('B2B')) return rotulo('B2B');
  if (m.startsWith('B2C')) return rotulo('CartãoSiM');
  return { texto: 'Particular', particular: true };
}

export type FiltroStatusConta = 'todos' | 'provisorio' | 'definitivo' | 'definitivo_saldo';

// mesmas regras do contarStatus/contasFiltradas do dashboard
export function filtrarContasPorStatus(contas: ContaPacienteResumo[], filtro: FiltroStatusConta): ContaPacienteResumo[] {
  const definitiva = (c: ContaPacienteResumo) => !!c.statusConta?.toLowerCase().includes('definit');
  switch (filtro) {
    case 'provisorio': return contas.filter(c => !c.statusConta || c.statusConta.toLowerCase().includes('provis'));
    case 'definitivo': return contas.filter(definitiva);
    case 'definitivo_saldo': return contas.filter(c => definitiva(c) && c.vlConta - c.vlPago > 0.01);
    default: return contas;
  }
}
